import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export type ShipmentRouteStop = {
  key: string;
  kind: 'loading' | 'unloading' | 'pickup' | 'delivery';
  label: string;
  address?: string | null;
  city?: string | null;
  district?: string | null;
  stopOrder?: number;
  lat: number;
  lon: number;
};

export type ShipmentRouteStep = {
  instruction: string;
  name?: string | null;
  distance_m?: number;
  duration_s?: number;
  lat: number;
  lon: number;
  leg?: number;
};

export type ShipmentRouteCheckpoint = {
  lat: number;
  lon: number;
  distance_km: number;
};

export type ShipmentRouteData = {
  shipmentId: number;
  shipment_no?: string | null;
  profile?: string | null;
  source?: string | null;
  stops?: ShipmentRouteStop[];
  truckParams?: Record<string, string | number | boolean>;
  distance_km?: number | null;
  duration_h?: number | null;
  pointCount?: number | null;
  simplifiedPointCount?: number | null;
  checkpoints?: ShipmentRouteCheckpoint[];
  steps?: ShipmentRouteStep[];
  stepCount?: number | null;
  computedAt?: string | null;
  geometry?: { type: 'LineString'; coordinates: [number, number][] } | null;
  simplifiedGeometry?: { type: 'LineString'; coordinates: [number, number][] } | null;
  google_maps_url?: string | null;
};

export const formatStepDistance = (meters: number | null | undefined): string | null => {
  const value = Number(meters);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
};

const LOADING_COLOR = '#dc2626';
const UNLOADING_COLOR = '#16a34a';
const PASSING_POINT_COLOR = '#2563eb';
const ROUTE_COLOR = '#0f172a';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export function ShipmentRouteMap({
  route,
  currentPosition,
  className = 'h-[420px] w-full',
}: {
  route: ShipmentRouteData | null;
  currentPosition?: { lat: number; lon: number; label?: string } | null;
  className?: string;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Layer[]>([]);
  const [showSteps, setShowSteps] = useState(true);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { center: [39.0, 35.0], zoom: 6, zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // ORS geometrisi [lon, lat] sırasındadır; Leaflet [lat, lon] bekler.
  const latLngLine = useMemo<[number, number][]>(() => {
    const source = route?.simplifiedGeometry?.coordinates || route?.geometry?.coordinates || [];
    return source
      .filter((pair) => Array.isArray(pair) && pair.length >= 2)
      .map(([lon, lat]) => [Number(lat), Number(lon)] as [number, number])
      .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));
  }, [route]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    layersRef.current.forEach((layer) => layer.remove());
    layersRef.current = [];

    const stops = route?.stops || [];
    if (latLngLine.length > 1) {
      // Ana hat kalın, altında ince açık şerit: yoğun TIR güzergâhlarında okunurluk.
      const halo = L.polyline(latLngLine, { color: '#ffffff', weight: 9, opacity: 0.75 }).addTo(map);
      const line = L.polyline(latLngLine, { color: ROUTE_COLOR, weight: 4, opacity: 0.9 }).addTo(map);
      layersRef.current.push(halo, line);
    }

    stops.forEach((stop, index) => {
      const isLoading = stop.kind === 'loading' || stop.kind === 'pickup';
      const color = isLoading ? LOADING_COLOR : UNLOADING_COLOR;
      const label = escapeHtml(stop.label || stop.key);

      const marker = L.circleMarker([stop.lat, stop.lon], {
        radius: 8,
        fillColor: color,
        fillOpacity: 0.95,
        color: '#ffffff',
        weight: 2.5,
      }).addTo(map);
      marker.bindTooltip(
        `<div style="font-family:system-ui,-apple-system,sans-serif;font-size:12px">
           <div style="font-weight:700">${index + 1}. ${label}</div>
           <div style="color:#64748b;margin-top:2px">${isLoading ? 'Yükleme' : 'Teslim'}</div>
         </div>`,
        { direction: 'top', offset: [0, -8] },
      );
      layersRef.current.push(marker);

      const indexIcon = L.divIcon({
        className: '',
        html: `<div style="display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:999px;background:${color};color:#fff;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);font-family:system-ui,-apple-system,sans-serif">${index + 1}</div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      layersRef.current.push(L.marker([stop.lat, stop.lon], { icon: indexIcon, interactive: false }).addTo(map));
    });

    if (showSteps) {
      const checkpoints = (route?.checkpoints || [])
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon));
      checkpoints.forEach((point, index) => {
          const marker = L.circleMarker([point.lat, point.lon], {
            radius: 5,
            fillColor: PASSING_POINT_COLOR,
            fillOpacity: 0.95,
            color: '#ffffff',
            weight: 1.5,
          }).addTo(map);
          marker.bindTooltip(
            `<div style="font-family:system-ui,-apple-system,sans-serif;font-size:12px;max-width:220px">
               <div style="font-weight:700">${index + 1}. Kontrol noktası</div>
               <div style="color:#64748b;margin-top:2px">Başlangıçtan yaklaşık ${escapeHtml(point.distance_km)} km</div>
             </div>`,
            { direction: 'top', offset: [0, -6] },
          );
          layersRef.current.push(marker);
      });
    }

    if (currentPosition) {
      const vehicleIcon = L.divIcon({
        className: '',
        html: '<div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:999px;background:#dc2626;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.38);font-size:19px;line-height:1">&#128666;</div>',
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
      const vehicle = L.marker([currentPosition.lat, currentPosition.lon], { icon: vehicleIcon, zIndexOffset: 500 }).addTo(map);
      vehicle.bindTooltip(`<div style="font-family:system-ui,-apple-system,sans-serif;font-size:12px;font-weight:700">${escapeHtml(
        currentPosition.label || 'Araç konumu',
      )}</div>`, { direction: 'top', offset: [0, -10] });
      layersRef.current.push(vehicle);
    }

    const boundsPoints = latLngLine.slice();
    stops.forEach((stop) => boundsPoints.push([stop.lat, stop.lon] as [number, number]));
    if (currentPosition) boundsPoints.push([currentPosition.lat, currentPosition.lon]);
    if (boundsPoints.length > 1) {
      map.fitBounds(L.latLngBounds(boundsPoints).pad(0.12));
    } else if (boundsPoints.length === 1) {
      map.setView(boundsPoints[0], 11);
    }
  }, [latLngLine, route, currentPosition, showSteps]);

  const checkpointCount = Array.isArray(route?.checkpoints) ? route.checkpoints.length : 0;

  return (
    <div className="relative">
      <div ref={mapRef} className={className} />
      {checkpointCount > 0 && (
        <button
          type="button"
          onClick={() => setShowSteps((value) => !value)}
          className={`absolute right-2 top-2 z-[1000] rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold shadow-sm transition-colors ${
            showSteps
              ? 'border-sky-300 bg-sky-600 text-white hover:bg-sky-700'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
          }`}
        >
          {showSteps ? 'Kontrol noktalarını gizle' : `${checkpointCount} kontrol noktası`}
        </button>
      )}
    </div>
  );
}

export function ShipmentRouteSummary({ route }: { route: ShipmentRouteData | null }) {
  if (!route) return null;
  const params = route.truckParams || {};
  const paramLabel = (key: string, unit: string) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return null;
    return `${value}${unit}`;
  };

  const chips = [
    paramLabel('weight', ' t'),
    paramLabel('height', ' m'),
    paramLabel('width', ' m'),
    paramLabel('length', ' m'),
    paramLabel('axleload', ' kg'),
    params.hazmat === true ? 'Tehlikeli madde' : null,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Mesafe</div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">
            {route.distance_km != null ? `${route.distance_km} km` : '-'}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Tahmini Süre</div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">
            {route.duration_h != null ? `${route.duration_h} saat` : '-'}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Durak</div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">{route.stops?.length ?? 0}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Profil</div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">TIR (HGV)</div>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {!!route.stops?.length && (
        <ol className="space-y-1.5">
          {route.stops.map((stop, index) => (
            <li key={stop.key} className="flex items-start gap-2 text-[12px]">
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                  stop.kind === 'loading' || stop.kind === 'pickup' ? 'bg-red-600' : 'bg-emerald-600'
                }`}
              >
                {index + 1}
              </span>
              <span className="text-slate-700 dark:text-slate-300">
                <span className="font-semibold">{stop.label}</span>
                {stop.district ? ` / ${stop.district}` : ''}
                <span className="ml-1.5 text-[11px] text-slate-400">
                  {stop.kind === 'loading' || stop.kind === 'pickup' ? 'Yükleme' : 'Teslim'}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}

      {!!route.steps?.length && (
        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Rota Tarifi ({route.steps.length} adım)
          </div>
          <ol className="max-h-64 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
            {route.steps.map((step, index) => {
              const distance = formatStepDistance(step.distance_m);
              const legLabel =
                Array.isArray(route.stops) && step.leg != null && step.leg + 1 < route.stops.length
                  ? `${route.stops[step.leg].label} → ${route.stops[step.leg + 1].label}`
                  : null;
              return (
                <li key={index} className="flex items-start gap-2 px-3 py-1.5 text-[12px]">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[10px] font-bold text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-700 dark:text-slate-200">{step.instruction}</div>
                    {step.name ? (
                      <div className="truncate text-[11px] font-semibold text-slate-400">{step.name}</div>
                    ) : null}
                    {legLabel ? (
                      <div className="text-[11px] text-slate-400">{legLabel}</div>
                    ) : null}
                  </div>
                  {distance ? (
                    <span className="shrink-0 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      {distance}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
